import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../../styles/LandingPage.css';
import './appointment-confirmation-page.css';
import { confirmAppointmentByToken, getAppointmentConfirmationStatus } from '../../../api/storefront/client';

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.3l2.3 2.3 4.7-5.1" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <circle cx="12" cy="16.5" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5l3 1.8" />
  </svg>
);

const XCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6" />
    <path d="M15 9l-6 6" />
  </svg>
);

const statusIcons = {
  already_confirmed: CheckCircleIcon,
  expired: ClockIcon,
  invalid: XCircleIcon,
  unavailable: AlertCircleIcon
};

const stateLabels = {
  confirmable: {
    title: 'Confirm your appointment',
    message: 'Tap the button below to confirm your appointment.'
  },
  already_confirmed: {
    title: 'Appointment confirmed',
    message: 'Your appointment has already been confirmed. Thank you.'
  },
  expired: {
    title: 'Link expired',
    message: 'This confirmation link has expired.'
  },
  invalid: {
    title: 'Link unavailable',
    message: 'This confirmation link is no longer valid.'
  },
  unavailable: {
    title: 'Confirmation unavailable',
    message: 'This appointment can no longer be confirmed from this link.'
  }
};

const StorefrontAppointmentConfirmationPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [companyName, setCompanyName] = useState('Appointment Confirmation');
  const [pageState, setPageState] = useState('confirmable');
  const [message, setMessage] = useState(stateLabels.confirmable.message);

  useEffect(() => {
    let isCancelled = false;

    const loadStatus = async () => {
      setLoading(true);
      try {
        const response = await getAppointmentConfirmationStatus(token);
        if (isCancelled) return;

        const state = response?.state || 'invalid';
        setPageState(state);
        setMessage(response?.message || stateLabels[state]?.message || stateLabels.invalid.message);
        setCompanyName(response?.companyName || 'Appointment Confirmation');
      } catch (error) {
        if (isCancelled) return;
        setPageState('invalid');
        setMessage('This confirmation link is no longer valid.');
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadStatus();
    return () => { isCancelled = true; };
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const response = await confirmAppointmentByToken(token);
      const state = response?.state || 'already_confirmed';
      setPageState(state);
      setMessage(response?.message || 'Your appointment has been confirmed. Thank you.');
    } catch (error) {
      setPageState('invalid');
      setMessage('We could not confirm this appointment from the link.');
    } finally {
      setConfirming(false);
    }
  };

  const displayCopy = stateLabels[pageState] || stateLabels.invalid;
  const StatusIcon = statusIcons[pageState] || AlertCircleIcon;
  const shouldShowStatusIcon = pageState !== 'confirmable';

  return (
    <div className="landing-page">
      <div className="appointment-confirmation-page">
        <div className="appointment-confirmation-page__card">
          <div className="appointment-confirmation-page__eyebrow">{companyName}</div>
          {loading ? (
            <>
              <div className="appointment-confirmation-page__spinner" aria-hidden="true" />
              <p className="appointment-confirmation-page__message">Checking your confirmation link…</p>
            </>
          ) : (
            <>
              {shouldShowStatusIcon && (
                <div className={`appointment-confirmation-page__status-icon appointment-confirmation-page__status-icon--${pageState}`}>
                  <StatusIcon />
                </div>
              )}
              <h1 className="appointment-confirmation-page__title">{displayCopy.title}</h1>
              <p className="appointment-confirmation-page__message">{message}</p>
              {pageState === 'confirmable' && (
                <button
                  type="button"
                  className="appointment-confirmation-page__button"
                  onClick={handleConfirm}
                  disabled={confirming}
                >
                  {confirming ? 'Confirming…' : 'Confirm appointment'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorefrontAppointmentConfirmationPage;
